<template>
  <section class="ops-tracker-page">
    <b-card class="mb-2">
      <h5 class="ops-filter-label mb-1">Message</h5>
      <b-form-group label="Subject" label-for="bulk-subject" label-class="ops-filter-label">
        <b-form-input id="bulk-subject" v-model="subject" placeholder="Enter email subject" />
      </b-form-group>
      <b-form-group label="Mail body" label-for="bulk-body" label-class="ops-filter-label" class="mb-0">
        <quill-editor id="bulk-body" v-model="mailBody" :options="editorOptions" />
        <small class="text-muted d-block mt-1">Use placeholders like &lt;&lt;name&gt;&gt; or &lt;&lt;email&gt;&gt; to personalize from the spreadsheet columns.</small>
      </b-form-group>
    </b-card>

    <b-card class="mb-2">
      <h5 class="ops-filter-label mb-1">Sender &amp; recipients</h5>
      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Sender name" label-for="bulk-sender-name" label-class="ops-filter-label">
            <b-form-input id="bulk-sender-name" v-model="senderName" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Sender email" label-for="bulk-sender-email" label-class="ops-filter-label">
            <b-form-input id="bulk-sender-email" v-model="senderEmail" type="email" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Recipient file" label-for="bulk-file" label-class="ops-filter-label">
            <div class="d-flex align-items-center flex-wrap">
              <b-form-file
                id="bulk-file"
                class="flex-grow-1 mr-1"
                accept=".xls,.xlsx,.csv"
                placeholder="Choose Excel or CSV"
                @change="handleFileUpload"
              />
              <b-button variant="link" class="px-0" @click.prevent="downloadSampleCsv">Download sample CSV</b-button>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <div class="d-flex flex-wrap align-items-center">
        <b-button variant="primary" class="mr-1 mb-1" :disabled="isSending" @click="sendMails">
          Send
        </b-button>
        <b-button variant="outline-secondary" class="mr-1 mb-1" :disabled="isSending" @click="previewMail">
          Mail preview
        </b-button>
        <b-button
          v-if="reportReady"
          size="sm"
          variant="outline-success"
          class="ops-icon-btn mb-1"
          v-b-tooltip.hover
          title="Download Report"
          aria-label="Download Report"
          @click="downloadReport"
        >
          <feather-icon icon="DownloadIcon" size="16" />
        </b-button>
      </div>
    </b-card>

    <b-card>
      <div class="ops-toolbar mb-2">
        <div class="ops-count">
          Recipients: {{ excelData.length }}
          <span v-if="isSending" class="ops-page-subtitle ml-1">Sending {{ sendProgress }} of {{ excelData.length }}…</span>
        </div>
      </div>

      <div v-if="isLoading" class="ops-loading">
        <b-spinner variant="primary" label="Loading recipients" />
        <p class="ops-loading-text mt-1">Reading spreadsheet…</p>
      </div>

      <div v-else-if="isSending" class="ops-loading mb-2">
        <b-spinner variant="primary" label="Sending emails" />
        <p class="ops-loading-text mt-1">Sending emails, please wait… {{ sendProgress }} of {{ excelData.length }}</p>
      </div>

      <div v-else-if="!excelData.length" class="ops-empty">
        <h6 class="ops-empty-title">No recipients loaded</h6>
        <p class="ops-empty-hint">Upload an Excel or CSV file with at least an email column to preview and send.</p>
      </div>

      <div v-else class="ops-table-wrap">
        <b-table
          class="ops-table"
          responsive
          hover
          :items="excelData"
          :fields="tableFields"
          :per-page="perPage"
          :current-page="currentPage"
          :busy="isLoading || isSending"
          show-empty
        >
          <template #cell(status)="data">
            <b-badge
              v-if="data.item.status"
              pill
              :class="data.item.status === 'Success' ? 'badge-success' : 'badge-danger'"
            >
              {{ data.item.status }}
            </b-badge>
            <span v-else class="text-muted">Pending</span>
          </template>
          <template #cell(errorMessage)="data">
            <span class="text-danger">{{ data.item.errorMessage }}</span>
          </template>
        </b-table>
      </div>
      <b-pagination
        v-if="excelData.length > perPage"
        v-model="currentPage"
        size="sm"
        class="ops-pagination"
        :total-rows="excelData.length"
        :per-page="perPage"
        align="right"
      />
    </b-card>

    <b-modal id="mail-preview-modal" title="Mail preview" size="lg" hide-footer>
      <div v-html="previewContent" />
    </b-modal>
  </section>
</template>

<script>
import {
  BFormGroup,
  BFormInput,
  BFormFile,
  BButton,
  BTable,
  BPagination,
  BSpinner,
  BModal,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue';
import { quillEditor } from 'vue-quill-editor';
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver';
import MailService from '@/services/mail-service';
import MassMailer from '@/services/massMailer-service'

export default {
  components: {
    BFormGroup,
    BFormInput,
    BFormFile,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BModal,
    quillEditor,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      subject: '',
      mailBody: '',
      senderName: '',
      senderEmail: '',
      senderEmpId: '',
      excelData: [],
      tableFields: [],
      currentPage: 1,
      perPage: 25,
      isLoading: false,
      isSending: false,
      sendProgress: 0,
      reportReady: false,
      editorOptions: {},
      previewContent: '',
    };
  },
  methods: {
    downloadSampleCsv() {
      const header = 'email,name';
      const rows = [
        'recipient1@example.com,Recipient One',
        'recipient2@example.com,Recipient Two',
        'recipient3@example.com,Recipient Three',
      ];
      const csvContent = [header, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'bulk_mailer_recipients_sample.csv');
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.excelData = []
      this.tableFields = []
      this.reportReady = false
      this.currentPage = 1
      if (file) {
        this.readExcelFile(file);
      }
    },
    readExcelFile(file) {
      this.isLoading = true;
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

        this.excelData = jsonData.map((item) => {
          const normalizedItem = {};
          for (const key in item) {
            const normalizedKey = key.trim().toLowerCase();
            normalizedItem[normalizedKey] = item[key];
          }
          return {
            ...normalizedItem,
            status: '',
            errorMessage: '',
          };
        });

        const reserved = new Set(['status', 'errormessage']);
        const dataKeys = this.excelData.length
          ? Object.keys(this.excelData[0]).filter((key) => !reserved.has(key))
          : [];
        this.tableFields = [
          ...dataKeys.map((key) => ({ key, label: key })),
          { key: 'status', label: 'Status' },
          { key: 'errorMessage', label: 'Error Message' },
        ];
        this.isLoading = false;
      };
      reader.onerror = () => {
        this.isLoading = false;
        this.$bvToast.toast('Could not read the selected file', {
          title: 'Error',
          variant: 'danger',
        });
      };
      reader.readAsArrayBuffer(file);
    },
    async previewMail() {
      if (this.excelData.length > 0) {
        const firstRecord = this.excelData[0];
        const previewData = await this.replacePlaceholders(this.mailBody, firstRecord);
        this.previewContent = previewData;
        this.$bvModal.show('mail-preview-modal');
      } else {
        this.$bvToast.toast('No data to preview', {
          title: 'Error',
          variant: 'danger',
        });
      }
    },
    async replacePlaceholders(template, data) {
      let result = template;
      for (const key in data) {
        result = result.replaceAll(`&lt;&lt;${key}&gt;&gt;`, data[key]);
      }
      return result;
    },
    async sendMails() {
      if (!this.isLoggedIn()) {
        return
      }
      if (!this.senderEmail || !this.senderName || !this.subject || !this.excelData.length) {
        this.$bvToast.toast('Subject, Sender Name, Email and Recipient List are required', {
          title: 'Error',
          variant: 'danger',
        });
        return;
      }
      this.isSending = true;
      this.reportReady = false;
      this.sendProgress = 0;
      const mailer_id = new Date()
      for (let i = 0; i < this.excelData.length; i++) {
        const recipient = this.excelData[i];
        this.sendProgress = i + 1;
        const personalizedBody = await this.replacePlaceholders(this.mailBody, recipient);
        try {
          const data = {
            senderName: this.senderName,
            senderEmail: this.senderEmail,
            recipientEmail: recipient.email,
            subject: this.subject,
            bodyContent: personalizedBody,
          }

          const res = await MailService.sendEmail(data);
          if (res && res.status === 200) {
            this.$set(this.excelData[i], 'status', 'Success');
            MassMailer.createMailer({
              sender_id: this.senderEmpId,
              mail_sent: true,
              response: '',
              mailer_id: mailer_id,
              source: "ATS BULK MAIL",
              comment: recipient.email + ': ' + this.subject
            }).catch(() => {})
          } else {
            const errorMessage = (res && res.data && res.data.error && res.data.error.message)
              || (res && res.data)
              || 'Failed to send';
            this.$set(this.excelData[i], 'status', 'Failed');
            this.$set(this.excelData[i], 'errorMessage', errorMessage);
            MassMailer.createMailer({
              sender_id: this.senderEmpId,
              mail_sent: false,
              response: errorMessage,
              mailer_id: mailer_id,
              source: "ATS BULK MAIL",
              comment: recipient.email + ': ' + this.subject
            }).catch(() => {})
          }
        } catch (error) {
          const errorMessage = (error && error.response && error.response.data) || (error && error.message) || 'Failed to send';
          this.$set(this.excelData[i], 'status', 'Failed');
          this.$set(this.excelData[i], 'errorMessage', errorMessage);
          MassMailer.createMailer({
            sender_id: this.senderEmpId,
            mail_sent: false,
            response: errorMessage,
            mailer_id: mailer_id,
            source: "ATS BULK MAIL",
            comment: recipient.email + ': ' + this.subject
          }).catch(() => {})
        }
        await this.sleep(600);
      }
      this.isSending = false;
      this.reportReady = true;
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    downloadReport() {
      const worksheetData = this.excelData.map((item) => {
        const { __ob__, ...exportItem } = item;
        return exportItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Mail Status Report');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const dateNow = new Date()
      saveAs(blob, `Mail_Status_Report_${dateNow}.xlsx`);
    },
    isLoggedIn() {
      const expireIn = localStorage.getItem('expireIn')
      if (Math.floor(Date.now() / 1000) > Number(expireIn)) {
        window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
        localStorage.clear();
        this.$router.push({ name: "auth-login" });
        return false
      }
      return true
    },
  },
  mounted() {
    this.isLoggedIn()
    const userData = JSON.parse(localStorage.getItem('userData'))
    if (userData) {
      this.senderName = userData.first_name + ' ' + userData.last_name
      this.senderEmail = userData.email
      this.senderEmpId = userData.emp_id
    }
  }
};
</script>
<style lang="scss">
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/apps/ops-tracker.scss';
</style>
